import { fetchCallCenterDashboard } from '@/api/callcenter/dashboard/getDashboard';
import { Button } from '@/components/ui/elements/button';
import { toast } from '@/components/ui/elements/use-toast';
import { AuthContext } from '@/providers/auth';
import { ArrowRightCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useContext, useState } from 'react';
import { useOpenAssignment } from '../hooks/useOpenAssignment';
import { currentLeadIdFromPath, pickNextAssignment } from '../logic';

/**
 * Occupies the header slot the legacy wait-time widget used to hold. That
 * widget's Next/Longest/Newest/Oldest links browsed the franchise-wide inbox,
 * which is what the assignment queue replaced — but agents still want the one
 * useful thing it did, which is jumping straight to the next lead without
 * routing back through the dashboard first.
 *
 * The queue is fetched on click rather than subscribed to. A live query here
 * would poll from every page in the app to keep a count nobody asked for.
 */
export const NextLeadButton = () => {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { user } = useContext(AuthContext);
  const { openAssignment } = useOpenAssignment();
  const [loading, setLoading] = useState(false);

  // Same population as the dashboard swap and the WaitTime gate it replaces:
  // consumer call-center agents. Franchise users keep the legacy widget.
  if (!user?.is_callcenter || user.is_b2b) return <></>;

  const goToNextLead = async () => {
    setLoading(true);

    try {
      const dashboard = await fetchCallCenterDashboard();
      const queue = dashboard?.queue ?? [];
      const currentLeadId = currentLeadIdFromPath(pathname);
      const next = pickNextAssignment(queue, currentLeadId);

      // openAssignment, not a bare navigate: arriving at a lead detail screen
      // does not claim anything by itself, so jumping straight there would
      // leave the assignment merely offered — no "working from your queue"
      // banner, no activity pings, and its accept deadline still running
      // against an agent who is sitting on the lead.
      if (next) {
        openAssignment(next);

        return;
      }

      // Nothing to move to, but the queue isn't empty — so the agent stays
      // put. Bouncing them to the dashboard here would read as the button
      // having thrown their work away. Two different reasons land here and
      // an agent can act on the difference: their own lead is the only one,
      // or the rest are locked by franchise owners right now.
      if (queue.length > 0) {
        const othersExist = queue.some(
          (item) => item.lead && item.lead.lead_id !== Number(currentLeadId),
        );

        toast(
          othersExist
            ? {
                title: 'The rest of your queue is on hold',
                description: 'Those leads are being worked by their franchise owners right now.',
              }
            : {
                title: 'This is your only assigned lead',
                description: 'Finish or release it and the feed will top your queue back up.',
              },
        );

        return;
      }

      toast({
        title: 'Your queue is empty',
        description: 'Opening your dashboard — new leads land there as the feed tops up.',
      });

      navigate({ to: '/dashboard' });
    } catch {
      // fetchCallCenterDashboard is configured to skip the global error toast,
      // so without this the click would fail silently.
      toast({
        variant: 'destructive',
        title: 'Could not load your queue',
        description: 'Check your connection and try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      size="sm"
      onClick={() => void goToNextLead()}
      disabled={loading}
      aria-busy={loading}
      className="w-full rounded-none md:w-fit md:rounded-full"
    >
      <ArrowRightCircleIcon className="h-5 w-5" aria-hidden="true" />
      <span>{loading ? 'Finding next lead…' : 'Next Lead'}</span>
    </Button>
  );
};
